import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TotalQuantityUpdated } from "@services/drybulkCargoOperationsApi";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useUpdateTotalQuantity } from "../hooks/useCargoOperationMutations";
import { problemDetail } from "../utils/problemDetail";
import OperationTotals from "./OperationTotals";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.add`;

// Non-negative, up to 3 decimals: the column is NUMERIC(14,3). A shape check on the text the user
// typed - not arithmetic (HLD section 5.3).
const TOTAL_QUANTITY_PATTERN = /^\d{1,11}(\.\d{1,3})?$/;

const totalQuantitySchema = (t: (key: string) => string) =>
  z.object({
    totalQuantity: z
      .string()
      .trim()
      .regex(TOTAL_QUANTITY_PATTERN, t(`${L}.validation.total-quantity`)),
  });
type FormValues = z.infer<ReturnType<typeof totalQuantitySchema>>;

export interface TotalQuantityEditorHandle {
  /**
   * DBCO-0065 D6. Not editing, or unchanged: true with no call. Editing and changed: runs the same commit as
   * Save (same validation, same PATCH); true on success, false on a validation or server error (a server error is toasted).
   */
  commitPending: () => Promise<boolean>;
  /** DBCO-0065 D18: drops an unsaved edit and shows the saved total again. */
  discardEdit: () => void;
}

interface TotalQuantityEditorProps {
  operationId: string;
  totalQuantity: number | null;
  /** The server's figure, shown beside the total; never derived here. */
  remained: number | null;
  /** True where the operation's status still allows revising the total (R-48). */
  editable: boolean;
  /** False for Operations: only the total is shown, not the remained figure or its warning. */
  showRemained?: boolean;
  /** True inside the Operations info card, where the row carries the label. */
  inCard?: boolean;
  onSaved?: (updated: TotalQuantityUpdated) => void;
  /** DBCO-0031 D6: the host pauses its polling while the field is open. */
  onEditingChange?: (editing: boolean) => void;
}

const TotalQuantityEditor = forwardRef<TotalQuantityEditorHandle, TotalQuantityEditorProps>(
  function TotalQuantityEditor(
    {
      operationId,
      totalQuantity,
      remained,
      editable,
      showRemained = true,
      inCard = false,
      onSaved,
      onEditingChange,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const { notify } = useNotification();
    const schema = useMemo(() => totalQuantitySchema(t), [t]);
    const updateTotal = useUpdateTotalQuantity();
    const [editing, setEditingState] = useState(false);
    const committing = useRef(false);
    const form = useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: { totalQuantity: String(totalQuantity ?? "") },
    });
    const { handleSubmit, reset } = form;

    const editingRef = useRef(false);

    const setEditing = (open: boolean) => {
      editingRef.current = open;
      setEditingState(open);
      onEditingChange?.(open);
    };

    const commitReporting = async (): Promise<boolean> => {
      let succeeded = false;
      await handleSubmit(async ({ totalQuantity: typed }) => {
        if (committing.current) {
          return;
        }
        const value = Number(typed);
        if (totalQuantity === value) {
          setEditing(false);
          succeeded = true;
          return;
        }
        committing.current = true;
        try {
          const updated = await updateTotal.mutateAsync({ id: operationId, totalQuantity: value });
          onSaved?.(updated);
          setEditing(false);
          succeeded = true;
        } catch (error) {
          notify({
            type: "error",
            title: t("common.error"),
            description: problemDetail(error) ?? t(`${L}.save-failed`),
          });
        } finally {
          committing.current = false;
        }
      })();
      return succeeded;
    };
    const commit = () => commitReporting();

    useImperativeHandle(ref, () => ({
      commitPending: () => (editingRef.current ? commitReporting() : Promise.resolve(true)),
      discardEdit: () => {
        if (editingRef.current) {
          reset({ totalQuantity: String(totalQuantity ?? "") });
          setEditing(false);
        }
      },
    }));

    if (!editing) {
      return (
        <OperationTotals
          totalQuantity={totalQuantity}
          remained={remained}
          showRemained={showRemained}
          inCard={inCard}
          onEdit={
            editable
              ? () => {
                  reset({ totalQuantity: String(totalQuantity ?? "") });
                  setEditing(true);
                }
              : undefined
          }
        />
      );
    }

    return (
      <FormProvider {...form}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{ width: 280 }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void commit();
                }
              }}
            >
              <FormItem<FormValues, "totalQuantity">
                name="totalQuantity"
                type="text"
                label={t(`${L}.total-quantity`)}
                testId="drybulk-total-quantity"
                slotProps={{ htmlInput: { inputMode: "decimal" } }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
              <Button
                variant="contained"
                testId="drybulk-cargo-operation-total-save"
                onClick={() => void commit()}
                label={<span className="normal-case font-medium">{t(`${L}.save`)}</span>}
              />
              <Button
                variant="text"
                testId="drybulk-cargo-operation-total-cancel"
                onClick={() => {
                  reset({ totalQuantity: String(totalQuantity ?? "") });
                  setEditing(false);
                }}
                label={<span className="normal-case font-medium">{t(`${L}.cancel`)}</span>}
              />
            </Box>
          </Box>
        </Box>
      </FormProvider>
    );
  },
);

export default TotalQuantityEditor;
